import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  TruckIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  XCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  MapPinIcon,
  UserIcon,
  WrenchScrewdriverIcon,
  ExclamationTriangleIcon,
  BuildingOfficeIcon,
  SignalIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
  BellIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'
import { 
  getVehicles, 
  getVehicle,
  createVehicle, 
  updateVehicle, 
  deleteVehicle,
  getFleetStats,
  getVehicleTracking,
  updateVehicleStatus,
  toggleVehicleTracking,
  type Vehicle,
  type CreateVehicleRequest,
  type UpdateVehicleRequest,
  type FleetStats,
  type VehicleTracking
} from '@/api/fleetApi'
import { getUsers, type User } from '@/api/usersApi'
import { institutionsApi, type Institution } from '@/api/institutionsApi'
import toast from 'react-hot-toast'

// Form schema for adding/editing vehicles
const vehicleSchema = z.object({
  vehicleNumber: z.string().min(3, 'Vehicle number must be at least 3 characters'),
  make: z.string().min(2, 'Make is required'),
  vehicleModel: z.string().min(2, 'Model is required'),
  year: z.number().min(1990, 'Year must be 1990 or later').max(new Date().getFullYear() + 2),
  capacity: z.number().min(1, 'Capacity must be at least 1').max(100, 'Capacity cannot exceed 100'),
  fuelType: z.enum(['petrol', 'diesel', 'electric', 'hybrid']),
  vehicleType: z.enum(['bus', 'van', 'car', 'minibus']),
  color: z.string().min(2, 'Color is required'),
  imei: z.string().min(15, 'IMEI must be at least 15 characters'),
  engineNumber: z.string().optional(),
  chassisNumber: z.string().optional(),
  registrationDate: z.string().min(1, 'Registration date is required'),
  assignedDriverId: z.string().optional(),
  institutionId: z.string().optional(), // For super admins
})

type VehicleFormData = z.infer<typeof vehicleSchema>

const FleetOverviewPage: React.FC = () => {
  const { user } = useAuth()
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [driverFilter, setDriverFilter] = useState<string>('all')
  const [institutionFilter, setInstitutionFilter] = useState<string>('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'table' | 'map'>('grid')
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalVehicles, setTotalVehicles] = useState(0)
  const [pageSize] = useState(12)
  
  // Available data for dropdowns
  const [drivers, setDrivers] = useState<User[]>([])
  const [institutions, setInstitutions] = useState<Institution[]>([])
  const [stats, setStats] = useState<FleetStats>({
    totalVehicles: 0,
    activeTrips: 0,
    maintenanceDue: 0,
    documentsExpiring: 0,
    activeVehicles: 0,
    maintenanceVehicles: 0,
    retiredVehicles: 0,
    outOfServiceVehicles: 0,
    buses: 0,
    vans: 0,
    cars: 0,
    minibuses: 0
  })
  const [trackingData, setTrackingData] = useState<VehicleTracking[]>([])
  const [lastTrackingUpdate, setLastTrackingUpdate] = useState<Date | null>(null)

  const vehicleForm = useForm<VehicleFormData>({
    resolver: zodResolver(vehicleSchema),
    defaultValues: {
      vehicleNumber: '',
      make: '',
      vehicleModel: '',
      year: new Date().getFullYear(),
      capacity: 50,
      fuelType: 'diesel',
      vehicleType: 'bus',
      color: '',
      imei: '',
      engineNumber: '',
      chassisNumber: '',
      registrationDate: '',
      assignedDriverId: '',
      institutionId: '',
    }
  })

  useEffect(() => {
    fetchVehicles()
    fetchDrivers()
    fetchStats()
    fetchTracking()
    if (user?.role === 'SUPER_ADMIN') {
      fetchInstitutions()
    }
  }, [currentPage, searchQuery, statusFilter, typeFilter, driverFilter, institutionFilter])

  // Real-time tracking updates every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchTracking()
      // Also refresh stats to show live trip counts
      fetchStats()
    }, 30000) // Update every 30 seconds

    return () => clearInterval(interval)
  }, [institutionFilter])

  const fetchVehicles = async () => {
    setLoading(true)
    try {
      console.log('Fetching vehicles with params:', {
        page: currentPage,
        limit: pageSize,
        search: searchQuery.trim() || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        vehicleType: typeFilter === 'all' ? undefined : typeFilter,
        assignedDriverId: driverFilter === 'all' ? undefined : driverFilter,
        institutionId: user?.role === 'SUPER_ADMIN' && institutionFilter !== 'all' ? institutionFilter : undefined
      });

      const response = await getVehicles({
        page: currentPage,
        limit: pageSize,
        search: searchQuery.trim() || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        vehicleType: typeFilter === 'all' ? undefined : typeFilter,
        assignedDriverId: driverFilter === 'all' ? undefined : driverFilter,
        institutionId: user?.role === 'SUPER_ADMIN' && institutionFilter !== 'all' ? institutionFilter : undefined
      })
      
      console.log('Fleet API response:', response)
      console.log('Vehicles data:', response.data?.vehicles)
      console.log('Pagination data:', response.data?.pagination)
      
      setVehicles(response.data?.vehicles || [])
      setTotalPages(response.data?.pagination?.totalPages || 1)
      setTotalVehicles(response.data?.pagination?.totalVehicles || 0)
    } catch (error) {
      console.error('Failed to fetch vehicles:', error)
      toast.error('Failed to load fleet data')
    } finally {
      setLoading(false)
    }
  }

  const fetchDrivers = async () => {
    try {
      const params = user?.role === 'SUPER_ADMIN' && institutionFilter !== 'all' 
        ? { institutionId: institutionFilter } 
        : {}
        
      const response = await getUsers(params)
      const driversData = response.users?.filter((user: User) => 
        user.role?.toLowerCase().includes('driver')
      ) || []
      
      setDrivers(driversData)
    } catch (error) {
      console.error('Failed to fetch drivers:', error)
    }
  }

  const fetchInstitutions = async () => {
    try {
      const response = await institutionsApi.getInstitutions()
      setInstitutions(response || [])
    } catch (error) {
      console.error('Failed to fetch institutions:', error)
    }
  }

  const fetchStats = async () => {
    try {
      const statsInstitutionId = user?.role === 'SUPER_ADMIN' && institutionFilter !== 'all' 
        ? institutionFilter 
        : undefined
        
      const response = await getFleetStats(statsInstitutionId)
      setStats(response.data || {})
    } catch (error) {
      console.error('Failed to fetch fleet stats:', error)
    }
  }

  const fetchTracking = async () => {
    try {
      const trackingInstitutionId = user?.role === 'SUPER_ADMIN' && institutionFilter !== 'all' 
        ? institutionFilter 
        : undefined
        
      const response = await getVehicleTracking(trackingInstitutionId)
      setTrackingData(response.data.vehicles || [])
      setLastTrackingUpdate(new Date())
    } catch (error) {
      console.error('Failed to fetch tracking data:', error)
    }
  }

  const handleCreateVehicle = async (data: VehicleFormData) => {
    try {
      const vehicleData: CreateVehicleRequest = {
        ...data,
        registrationDate: new Date(data.registrationDate).toISOString(),
      }

      // Remove empty optional fields to avoid ObjectId casting errors
      if (!data.assignedDriverId || data.assignedDriverId.trim() === '') {
        delete vehicleData.assignedDriverId
      }
      if (!data.engineNumber || data.engineNumber.trim() === '') {
        delete vehicleData.engineNumber
      }
      if (!data.chassisNumber || data.chassisNumber.trim() === '') {
        delete vehicleData.chassisNumber
      }

      // Add institution ID for super admin
      if (user?.role === 'SUPER_ADMIN' && data.institutionId) {
        vehicleData.institutionId = data.institutionId
      }

      await createVehicle(vehicleData)
      toast.success('Vehicle added to fleet successfully!')
      setShowAddModal(false)
      vehicleForm.reset()
      fetchVehicles()
      fetchStats()
    } catch (error: any) {
      console.error('Failed to create vehicle:', error)
      const message = error?.response?.data?.message || 'Failed to add vehicle to fleet'
      toast.error(message)
    }
  }

  const handleUpdateVehicle = async (data: VehicleFormData) => {
    if (!selectedVehicle) return

    try {
      const updateData: UpdateVehicleRequest = {
        ...data,
      }

      // Remove empty optional fields to avoid ObjectId casting errors
      if (!data.assignedDriverId || data.assignedDriverId.trim() === '') {
        delete updateData.assignedDriverId
      }
      if (!data.engineNumber || data.engineNumber.trim() === '') {
        delete updateData.engineNumber
      }
      if (!data.chassisNumber || data.chassisNumber.trim() === '') {
        delete updateData.chassisNumber
      }

      await updateVehicle(selectedVehicle.id, updateData)
      toast.success('Vehicle updated successfully!')
      setShowEditModal(false)
      setSelectedVehicle(null)
      vehicleForm.reset()
      fetchVehicles()
    } catch (error: any) {
      console.error('Failed to update vehicle:', error)
      const message = error?.response?.data?.message || 'Failed to update vehicle'
      toast.error(message)
    }
  }

  const handleDeleteVehicle = async (vehicle: Vehicle) => {
    if (confirm(`Are you sure you want to remove ${vehicle.vehicleNumber} from the fleet?`)) {
      try {
        await deleteVehicle(vehicle.id)
        toast.success('Vehicle removed from fleet successfully!')
        fetchVehicles()
        fetchStats()
      } catch (error: any) {
        const message = error?.response?.data?.message || 'Failed to remove vehicle'
        toast.error(message)
      }
    }
  }

  const handleStatusUpdate = async (vehicle: Vehicle, newStatus: Vehicle['status']) => {
    try {
      await updateVehicleStatus(vehicle.id, newStatus)
      toast.success(`Vehicle ${vehicle.vehicleNumber} status updated to ${newStatus}`)
      fetchVehicles()
      fetchStats()
    } catch (error) {
      toast.error('Failed to update vehicle status')
    }
  }

  const handleToggleTracking = async (vehicle: Vehicle) => {
    try {
      await toggleVehicleTracking(vehicle.id, !vehicle.isTracking)
      toast.success(`GPS tracking ${vehicle.isTracking ? 'disabled' : 'enabled'} for ${vehicle.vehicleNumber}`)
      fetchVehicles()
      fetchTracking()
    } catch (error) {
      toast.error('Failed to toggle GPS tracking')
    }
  }

  const handleEditVehicle = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle)
    vehicleForm.reset({
      vehicleNumber: vehicle.vehicleNumber,
      make: vehicle.make,
      vehicleModel: vehicle.vehicleModel,
      year: vehicle.year,
      capacity: vehicle.capacity,
      fuelType: vehicle.fuelType,
      vehicleType: vehicle.vehicleType,
      color: vehicle.color,
      imei: vehicle.imei || '',
      engineNumber: vehicle.engineNumber || '',
      chassisNumber: vehicle.chassisNumber || '',
      registrationDate: vehicle.registrationDate ? new Date(vehicle.registrationDate).toISOString().split('T')[0] : '',
      assignedDriverId: vehicle.assignedDriverId || '',
    })
    setShowEditModal(true)
  }

  const getStatusBadge = (status: Vehicle['status']) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      maintenance: { color: 'bg-yellow-100 text-yellow-800', label: 'Maintenance' },
      retired: { color: 'bg-gray-100 text-gray-800', label: 'Retired' },
      out_of_service: { color: 'bg-red-100 text-red-800', label: 'Out of Service' }
    }

    const config = statusConfig[status]
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.label}
      </span>
    )
  }

  const getVehicleTypeIcon = (type: Vehicle['vehicleType']) => {
    const iconClass = "h-6 w-6"
    switch (type) {
      case 'bus': return <TruckIcon className={`${iconClass} text-blue-600`} />
      case 'van': return <TruckIcon className={`${iconClass} text-green-600`} />
      case 'car': return <TruckIcon className={`${iconClass} text-purple-600`} />
      case 'minibus': return <TruckIcon className={`${iconClass} text-orange-600`} />
      default: return <TruckIcon className={`${iconClass} text-gray-600`} />
    }
  }

  const isMaintenanceDue = (vehicle: Vehicle) => {
    if (!vehicle.maintenance?.nextServiceDate) return false
    return new Date(vehicle.maintenance.nextServiceDate) <= new Date()
  }

  const isDocumentExpiringSoon = (vehicle: Vehicle) => {
    if (!vehicle.documents?.length) return false
    const thirtyDaysFromNow = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
    return vehicle.documents.some(doc => new Date(doc.expiryDate) <= thirtyDaysFromNow)
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <TruckIcon className="h-8 w-8 text-blue-600 mr-3" />
            {user?.role === 'SUPER_ADMIN' ? 'Fleet Management' : 'Fleet Overview'}
          </h1>
          <p className="text-gray-600">
            {user?.role === 'SUPER_ADMIN' 
              ? 'Manage vehicles across all institutions' 
              : 'Monitor and manage your school\'s vehicle fleet'}
          </p>
          {lastTrackingUpdate && (
            <p className="text-xs text-green-600 flex items-center mt-1">
              <SignalIcon className="h-3 w-3 mr-1 animate-pulse" />
              Live tracking • Last updated: {lastTrackingUpdate.toLocaleTimeString()}
            </p>
          )}
        </div>
        <div className="mt-4 lg:mt-0 flex space-x-3">
          <Button
            onClick={() => setViewMode(viewMode === 'grid' ? 'table' : viewMode === 'table' ? 'map' : 'grid')}
            variant="secondary"
            size="sm"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            {viewMode === 'grid' ? 'Table View' : viewMode === 'table' ? 'Map View' : 'Grid View'}
          </Button>
          <Button onClick={() => setShowAddModal(true)} className="bg-blue-600 hover:bg-blue-700">
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Vehicle
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <TruckIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Fleet</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalVehicles}</p>
              <p className="text-xs text-gray-400">{stats.activeVehicles} active</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <SignalIcon className={`h-8 w-8 ${stats.activeTrips > 0 ? 'text-green-600 animate-pulse' : 'text-gray-400'}`} />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Active Trips</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeTrips}</p>
              <p className="text-xs text-gray-400">Currently running</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <WrenchScrewdriverIcon className={`h-8 w-8 ${stats.maintenanceDue > 0 ? 'text-orange-600' : 'text-gray-400'}`} />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Maintenance Due</p>
              <p className="text-2xl font-bold text-gray-900">{stats.maintenanceDue}</p>
              <p className="text-xs text-gray-400">Vehicles need service</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <ExclamationTriangleIcon className={`h-8 w-8 ${stats.documentsExpiring > 0 ? 'text-red-600' : 'text-gray-400'}`} />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Docs Expiring</p>
              <p className="text-2xl font-bold text-gray-900">{stats.documentsExpiring}</p>
              <p className="text-xs text-gray-400">Next 30 days</p>
            </div>
          </div>
        </div>
      </div>

      {/* Fleet Composition */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <TruckIcon className="h-5 w-5 text-blue-600 mr-2" />
            Fleet Composition
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{stats.buses}</p>
              <p className="text-sm text-gray-600">Buses</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{stats.vans}</p>
              <p className="text-sm text-gray-600">Vans</p>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-lg">
              <p className="text-2xl font-bold text-purple-600">{stats.cars}</p>
              <p className="text-sm text-gray-600">Cars</p>
            </div>
            <div className="text-center p-3 bg-orange-50 rounded-lg">
              <p className="text-2xl font-bold text-orange-600">{stats.minibuses}</p>
              <p className="text-sm text-gray-600">Minibuses</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
            Fleet Status
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Active Vehicles</span>
              <span className="px-2 py-1 text-sm bg-green-100 text-green-800 rounded-full">
                {stats.activeVehicles}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Under Maintenance</span>
              <span className="px-2 py-1 text-sm bg-yellow-100 text-yellow-800 rounded-full">
                {stats.maintenanceVehicles}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Out of Service</span>
              <span className="px-2 py-1 text-sm bg-red-100 text-red-800 rounded-full">
                {stats.outOfServiceVehicles}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Retired</span>
              <span className="px-2 py-1 text-sm bg-gray-100 text-gray-800 rounded-full">
                {stats.retiredVehicles}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search vehicles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-3 py-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="maintenance">Maintenance</option>
            <option value="out_of_service">Out of Service</option>
            <option value="retired">Retired</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Types</option>
            <option value="bus">Buses</option>
            <option value="van">Vans</option>
            <option value="car">Cars</option>
            <option value="minibus">Minibuses</option>
          </select>

          <select
            value={driverFilter}
            onChange={(e) => setDriverFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Drivers</option>
            <option value="">Unassigned</option>
            {drivers.map(driver => (
              <option key={driver.id || driver._id} value={driver.id || driver._id}>
                {driver.firstName} {driver.lastName}
              </option>
            ))}
          </select>

          {user?.role === 'SUPER_ADMIN' && (
            <select
              value={institutionFilter}
              onChange={(e) => setInstitutionFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Institutions</option>
              {institutions.map(institution => (
                <option key={institution.id} value={institution.id}>
                  {institution.name}
                </option>
              ))}
            </select>
          )}

          <Button
            onClick={() => {
              setSearchQuery('')
              setStatusFilter('all')
              setTypeFilter('all')
              setDriverFilter('all')
              setInstitutionFilter('all')
              setCurrentPage(1)
            }}
            variant="secondary"
            size="sm"
          >
            Clear Filters
          </Button>
        </div>
      </div>

      {/* Fleet List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : vehicles.length === 0 ? (
        <div className="text-center py-12">
          <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No vehicles</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by adding vehicles to your fleet.</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {vehicles.map((vehicle: any) => (
            <motion.div
              key={vehicle.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-md border hover:shadow-lg transition-shadow"
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    {getVehicleTypeIcon(vehicle.vehicleType)}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{vehicle.vehicleNumber}</h3>
                      <p className="text-sm text-gray-600">{vehicle.make} {vehicle.vehicleModel} ({vehicle.year})</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end space-y-1">
                    {getStatusBadge(vehicle.status)}
                    {vehicle.isTracking && (
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                        GPS Active
                      </span>
                    )}
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Capacity:</span>
                    <span className="text-gray-900 font-medium">{vehicle.capacity} seats</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Fuel Type:</span>
                    <span className="text-gray-900 font-medium capitalize">{vehicle.fuelType}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Driver:</span>
                    <span className="text-gray-900 font-medium">
                      {vehicle.assignedDriver 
                        ? `${vehicle.assignedDriver.firstName} ${vehicle.assignedDriver.lastName}`
                        : 'Unassigned'}
                    </span>
                  </div>
                  {vehicle.currentTrip && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Current Trip:</span>
                      <span className="text-green-600 font-medium capitalize">
                        {vehicle.currentTrip.tripType} - {vehicle.currentTrip.status}
                      </span>
                    </div>
                  )}
                </div>

                {/* Alerts */}
                <div className="mt-4 space-y-2">
                  {isMaintenanceDue(vehicle) && (
                    <div className="flex items-center text-xs text-orange-600">
                      <WrenchScrewdriverIcon className="h-3 w-3 mr-1" />
                      Maintenance due
                    </div>
                  )}
                  {isDocumentExpiringSoon(vehicle) && (
                    <div className="flex items-center text-xs text-red-600">
                      <DocumentTextIcon className="h-3 w-3 mr-1" />
                      Documents expiring soon
                    </div>
                  )}
                  {vehicle.currentLocation && (
                    <div className="flex items-center text-xs text-blue-600">
                      <MapPinIcon className="h-3 w-3 mr-1" />
                      {vehicle.currentLocation.address || `${vehicle.currentLocation.latitude}, ${vehicle.currentLocation.longitude}`}
                    </div>
                  )}
                </div>

                <div className="mt-4 flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleToggleTracking(vehicle)}
                      className={`p-2 rounded-lg transition-colors ${
                        vehicle.isTracking 
                          ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      title={vehicle.isTracking ? 'Disable GPS Tracking' : 'Enable GPS Tracking'}
                    >
                      <SignalIcon className="h-4 w-4" />
                    </button>
                    <select
                      value={vehicle.status}
                      onChange={(e) => handleStatusUpdate(vehicle, e.target.value as Vehicle['status'])}
                      className="text-xs px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="active">Active</option>
                      <option value="maintenance">Maintenance</option>
                      <option value="out_of_service">Out of Service</option>
                      <option value="retired">Retired</option>
                    </select>
                  </div>
                  
                  <div className="flex space-x-1">
                    <button
                      onClick={() => handleEditVehicle(vehicle)}
                      className="p-1 text-gray-400 hover:text-blue-600"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteVehicle(vehicle)}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : viewMode === 'table' ? (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vehicle</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Driver</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Trip</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Alerts</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {vehicles.map((vehicle: any) => (
                  <tr key={vehicle.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getVehicleTypeIcon(vehicle.vehicleType)}
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{vehicle.vehicleNumber}</div>
                          <div className="text-sm text-gray-500">{vehicle.make} {vehicle.vehicleModel}</div>
                          <div className="text-xs text-gray-400">{vehicle.capacity} seats • {vehicle.color}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {vehicle.assignedDriver 
                        ? `${vehicle.assignedDriver.firstName} ${vehicle.assignedDriver.lastName}`
                        : 'Unassigned'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(vehicle.status)}
                      {vehicle.isTracking && (
                        <div className="mt-1">
                          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                            GPS Active
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {vehicle.currentTrip ? (
                        <div>
                          <div className="font-medium text-gray-900 capitalize">
                            {vehicle.currentTrip.tripType}
                          </div>
                          <div className="text-xs text-gray-500 capitalize">
                            {vehicle.currentTrip.status}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400">No active trip</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        {isMaintenanceDue(vehicle) && (
                          <span className="flex items-center text-xs text-orange-600">
                            <WrenchScrewdriverIcon className="h-3 w-3 mr-1" />
                            Maintenance due
                          </span>
                        )}
                        {isDocumentExpiringSoon(vehicle) && (
                          <span className="flex items-center text-xs text-red-600">
                            <DocumentTextIcon className="h-3 w-3 mr-1" />
                            Docs expiring
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleToggleTracking(vehicle)}
                          className={`p-1 rounded ${
                            vehicle.isTracking 
                              ? 'text-green-600 hover:bg-green-100' 
                              : 'text-gray-400 hover:bg-gray-100'
                          }`}
                        >
                          <SignalIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditVehicle(vehicle)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteVehicle(vehicle)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        // Map view placeholder
        <div className="bg-white rounded-lg shadow border p-8 text-center">
          <MapPinIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">Fleet Map View</h3>
          <p className="mt-1 text-sm text-gray-500">
            Live vehicle tracking map - Coming soon!
          </p>
          <p className="text-xs text-gray-400 mt-2">
            {trackingData.length} vehicles with GPS tracking enabled
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white px-4 py-3 border rounded-lg">
          <div className="flex items-center">
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span> to{' '}
              <span className="font-medium">{Math.min(currentPage * pageSize, totalVehicles)}</span> of{' '}
              <span className="font-medium">{totalVehicles}</span> vehicles
            </p>
          </div>
          <div className="flex space-x-2">
            <Button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              variant="secondary"
              size="sm"
            >
              Previous
            </Button>
            <Button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              variant="secondary"
              size="sm"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Add/Edit Vehicle Modal */}
      <AnimatePresence>
        {(showAddModal || showEditModal) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                    <TruckIcon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      {showAddModal ? 'Add New Vehicle' : 'Edit Vehicle'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {showAddModal ? 'Add a vehicle to the fleet' : 'Update vehicle information'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowAddModal(false)
                    setShowEditModal(false)
                    setSelectedVehicle(null)
                    vehicleForm.reset()
                  }}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-white transition-colors"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <form
                onSubmit={vehicleForm.handleSubmit(showAddModal ? handleCreateVehicle : handleUpdateVehicle)}
                className="p-6 space-y-6"
              >
                {/* Institution Selection (Super Admin Only) */}
                {user?.role === 'SUPER_ADMIN' && showAddModal && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                      <BuildingOfficeIcon className="h-5 w-5 text-blue-600 mr-2" />
                      Institution
                    </h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Target Institution <span className="text-red-500">*</span>
                      </label>
                      <select
                        {...vehicleForm.register('institutionId')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select institution for this vehicle</option>
                        {institutions.map(institution => (
                          <option key={institution.id} value={institution.id}>
                            {institution.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Vehicle Information */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <TruckIcon className="h-5 w-5 text-blue-600 mr-2" />
                    Vehicle Information
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      {...vehicleForm.register('vehicleNumber')}
                      label="Vehicle Number"
                      placeholder="e.g., ABC 123Z, BUS-01"
                      error={vehicleForm.formState.errors.vehicleNumber?.message}
                      required
                    />
                    <Input
                      {...vehicleForm.register('make')}
                      label="Make"
                      placeholder="e.g., Toyota, Ford, Mercedes"
                      error={vehicleForm.formState.errors.make?.message}
                      required
                    />
                    <Input
                      {...vehicleForm.register('vehicleModel')}
                      label="Model"
                      placeholder="e.g., Hiace, Transit, Sprinter"
                      error={vehicleForm.formState.errors.vehicleModel?.message}
                      required
                    />
                    <Input
                      {...vehicleForm.register('year', { valueAsNumber: true })}
                      type="number"
                      label="Year"
                      placeholder="e.g., 2020"
                      error={vehicleForm.formState.errors.year?.message}
                      required
                    />
                    <Input
                      {...vehicleForm.register('capacity', { valueAsNumber: true })}
                      type="number"
                      label="Seating Capacity"
                      placeholder="e.g., 50"
                      error={vehicleForm.formState.errors.capacity?.message}
                      required
                    />
                    <Input
                      {...vehicleForm.register('color')}
                      label="Color"
                      placeholder="e.g., Blue, White, Yellow"
                      error={vehicleForm.formState.errors.color?.message}
                      required
                    />
                    <Input
                      {...vehicleForm.register('imei')}
                      label="IMEI"
                      placeholder="e.g., 123456789012345"
                      error={vehicleForm.formState.errors.imei?.message}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Vehicle Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        {...vehicleForm.register('vehicleType')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="bus">Bus</option>
                        <option value="van">Van</option>
                        <option value="car">Car</option>
                        <option value="minibus">Minibus</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fuel Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        {...vehicleForm.register('fuelType')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="petrol">Petrol</option>
                        <option value="diesel">Diesel</option>
                        <option value="electric">Electric</option>
                        <option value="hybrid">Hybrid</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Optional Details */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <DocumentTextIcon className="h-5 w-5 text-green-600 mr-2" />
                    Additional Details
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      {...vehicleForm.register('engineNumber')}
                      label="Engine Number"
                      placeholder="Optional engine number"
                      error={vehicleForm.formState.errors.engineNumber?.message}
                    />
                    <Input
                      {...vehicleForm.register('chassisNumber')}
                      label="Chassis Number"
                      placeholder="Optional chassis number"
                      error={vehicleForm.formState.errors.chassisNumber?.message}
                    />
                    <Input
                      {...vehicleForm.register('registrationDate')}
                      type="date"
                      label="Registration Date"
                      error={vehicleForm.formState.errors.registrationDate?.message}
                      required
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Assigned Driver
                      </label>
                      <select
                        {...vehicleForm.register('assignedDriverId')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">No driver assigned</option>
                        {drivers.map(driver => (
                          <option key={driver.id || driver._id} value={driver.id || driver._id}>
                            {driver.firstName} {driver.lastName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-between items-center pt-6 border-t bg-gray-50 -mx-6 -mb-6 px-6 py-4 rounded-b-lg">
                  <div className="text-xs text-gray-500">
                    {showAddModal ? (
                      <span className="flex items-center">
                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                        Vehicle will be added with "active" status
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <PencilIcon className="h-3 w-3 mr-1" />
                        Updating vehicle information
                      </span>
                    )}
                  </div>
                  
                  <div className="flex space-x-3">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setShowAddModal(false)
                        setShowEditModal(false)
                        setSelectedVehicle(null)
                        vehicleForm.reset()
                      }}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                      <TruckIcon className="h-4 w-4 mr-2" />
                      {showAddModal ? 'Add to Fleet' : 'Update Vehicle'}
                    </Button>
                  </div>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default FleetOverviewPage